// React Imports
import { useEffect, useState, useContext } from "react";
import {useAbility} from "@casl/react";
import dispatchService from "services/dispatch.service";
import { AuthContext } from "context";
import {AbilityContext} from "Can";

// E4A Components
import Footer from "e4acomponents/Footer";
import DashboardLayout from "e4acomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "e4acomponents/Navbars/DashboardNavbar";
import DataTable from "e4acomponents/Tables/DataTable";

// Dashboard & Material UI Components
import CancelTwoToneIcon from "@mui/icons-material/CancelTwoTone";
import CheckCircleTwoTone from "@mui/icons-material/CheckCircleTwoTone";
import DoneAllTwoToneIcon from '@mui/icons-material/DoneAllTwoTone';
import EventTwoToneIcon from '@mui/icons-material/EventTwoTone';
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import ArrowForwardIosTwoToneIcon from '@mui/icons-material/ArrowForwardIosTwoTone';
import DeleteTwoToneIcon from '@mui/icons-material/DeleteTwoTone';

// Custom Components and Dialogs
import ViewDispatchesProducts from "../components/view-dispatches-products";
import ApproveDispatchDialog from "./approve-dialog";
import RejectDispatchDialog from "./reject-dialog";
import ProvideDeliveryDateDialog from "./delivery-date-dialog";
import { convertDate } from "../components/helperFunctions";
import ConfirmDialog from "e4acomponents/ConfirmDialog";

export default function ManageDispatches() {
    const [dispatches, setDispatches] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [openDialog, setOpenDialog] = useState({});
    const [confirmDialog, setConfirmDialog] = useState({});
    const authContext = useContext(AuthContext);
    const ability = useAbility(AbilityContext);

    useEffect(() => {
        (async () => {
            setIsLoading(true);
            try {
                const response = await dispatchService.getDispatches();
                setDispatches(response.data);
                console.log("Fetched dispatches:", response.data);
            } catch (error) {
                console.error("Error fetching dispatches:", error);
            } finally {
                setIsLoading(false);
            }
        })();
    }, []);

    useEffect(() => {
        setTableData(getRows(dispatches));
    }, [dispatches]);

    // Transforms dispatch data into table rows
    const getRows = (dispatches) => {
        return dispatches.map((dispatch) => ({
            id: dispatch._id,
            dispatch_num: dispatch.dispatch_num,
            status: dispatch.status,
            statusChip: statusChip(dispatch.status),
            organization: dispatch.organization.name,
            req_date: convertDate(dispatch.req_date),
            createdAt: convertDate(dispatch.createdAt),
            description: dispatch.description,
            productIds: dispatch.products,
            ...(dispatch?.actual_delivery_date ? { actual_delivery_date: dispatch.actual_delivery_date } : {}),
        }));
    };

    // Defines the structure and content of the data table
    const dataTableData = {
        columns: [
            { Header: "Dispatch Number", accessor: "dispatch_num", align: "left", type: "number" },
            { Header: "Organization", accessor: "organization", align: "left" },
            { Header: "Request Date", accessor: "req_date", align: "left" },
            {
                Header: "Status",
                accessor: "status",
                align: "left",
                Cell: ({ row }) => row.original.statusChip,
                type: "singleSelect",
                valueOptions: ["Requested", "Approved", "Scheduled", "Rejected", "Completed"],
            },
            {
                Header: "Actions",
                accessor: "actions",
                align: "center",
                Cell: ({ row }) => {
                    const status = String(row.original.status || "").toLowerCase();
                    const canManage = ability.can('access', 'manage-dispatches');
                    const canApprove = (status === "requested") && canManage;
                    const canReject = (status === "requested") && canManage;
                    const canProvideDate = (status === "approved" || status === "scheduled") && canManage;
                    const canMarkDelivered = (status === "scheduled") && canManage;
                    const canDelete = ability.can('delete', 'dispatches');
                    const canView = true;

                    return (
                        <MDBox display="flex" alignItems="center" justifyContent="center">
                            <Tooltip title="Delete Dispatch">
                                <span>
                                    <IconButton
                                        onClick={() => handleOpenConfirmDialog(row.original, 'delete')}
                                        disabled={!canDelete}
                                    >
                                        <DeleteTwoToneIcon color={canDelete ? "error" : "disabled"} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Approve Dispatch Request">
                                <span>
                                    <IconButton
                                        onClick={() => handleOpenDialog('approve-dispatch', row.original)}
                                        disabled={!canApprove}
                                    >
                                        <CheckCircleTwoTone color={canApprove ? "success" : "disabled"} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Reject Dispatch Request">
                                <span>
                                    <IconButton
                                        onClick={() => handleOpenDialog('reject-dispatch', row.original)}
                                        disabled={!canReject}
                                    >
                                        <CancelTwoToneIcon color={canReject ? "error" : "disabled"} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Provide Delivery Date">
                                <span>
                                    <IconButton
                                        onClick={() => handleOpenDialog('provide-delivery-date', row.original)}
                                        disabled={!canProvideDate}
                                    >
                                        <EventTwoToneIcon color={canProvideDate ? "info" : "disabled"} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="Mark as Delivered">
                                <span>
                                    <IconButton
                                        onClick={() => markAsDelivered(row.original.id)}
                                        disabled={!canMarkDelivered}
                                    >
                                        <DoneAllTwoToneIcon color={canMarkDelivered ? "success" : "disabled"} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title="View Details">
                                <span>
                                    <IconButton
                                        onClick={() => handleOpenDialog('view-dispatch-products', row.original)}
                                        disabled={!canView}
                                    >
                                        <ArrowForwardIosTwoToneIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </MDBox>
                    );
                },
            },
        ],
        rows: tableData
    };

    // Returns a colored chip based on the status
    const statusChip = (status) => {
        const normalized = String(status || "").toLowerCase();

        const map = {
            requested: { color: "warning" },
            approved: { color: "success" },
            scheduled: { color: "info" },
            rejected: { color: "error" },
            completed: { color: "success" },
        };

        const variant = map[normalized];
        if (!variant) return null;

        return (
            <Chip variant="outlined" size="small" label={status} color={variant.color} />
        );
    }

    // Opens a dialog for a specific action
    const handleOpenDialog = (dialogName, data) => {
        setOpenDialog({ [dialogName]: { open: true, data } });
    }

    // Closes any open dialog
    const handleCloseDialog = (response) => {
        if (response?.reload) {
            // Reload dispatches
            reloadTableData();
        }
        setOpenDialog({});
    }

    // Reloads the table's data 
    const reloadTableData = async () => {
        (async () => {
            setIsLoading(true);
            try {
                const response = await dispatchService.getDispatches();
                setDispatches(response.data);
                console.log("Fetched dispatches:", response.data);
            } catch (error) {
                console.error("Error fetching dispatches:", error);
            } finally {
                setIsLoading(false);
            }
        })();
    }

    const handleCloseConfirmDialog = (response, title, id) => {
        if (response) {
            if (title === "Confirm Deletion") {
                deleteDispatch(id);
            }
        }
        setConfirmDialog({});
        setOpenDialog({ 'confirm-dialog': { open: false } });
    }

    const handleOpenConfirmDialog = (dispatch, action) => {
        let title, prompt;
        if (action === 'delete') {
            title = "Confirm Deletion";
            prompt = `Are you sure you want to delete dispatch ${dispatch.dispatch_num}? This action cannot be undone.`;
        }
        setConfirmDialog({
            title: title,
            prompt: prompt,
            id: dispatch.id,
            onClose: handleCloseConfirmDialog,
        })
        setOpenDialog({ 'confirm-dialog': { open: true } });
    }

    const deleteDispatch = async (dispatchId) => {
        try {
            const userId = await authContext.getCurrentUser();

            await dispatchService.deleteDispatch(dispatchId, userId);

            reloadTableData();
        } catch (error) {
            console.error("Error deleting dispatch:", error);
        }
    }

    // Marks a dispatch as completed
    const markAsDelivered = async (dispatchId) => {
        try {
            const payload = {
                data: {
                    attributes: {
                        status: "Completed",
                    }
                }
            }
            await dispatchService.updateDispatchStatus(dispatchId, payload);
            reloadTableData();
        } catch (error) {
            console.error("Error marking dispatch as delivered:", error);
        }
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            {/* Dialogs and Alerts */}
            <ViewDispatchesProducts
                open={openDialog['view-dispatch-products']?.open || false}
                handleClose={handleCloseDialog}
                dispatch={openDialog['view-dispatch-products']?.data || {}}
                productIds={openDialog['view-dispatch-products']?.data?.productIds || []}
            />

            <ApproveDispatchDialog
                open={openDialog['approve-dispatch']?.open || false}
                dispatchId={openDialog['approve-dispatch']?.data.id || {}}
                handleClose={handleCloseDialog}
            />

            <RejectDispatchDialog
                open={openDialog['reject-dispatch']?.open || false}
                dispatchId={openDialog['reject-dispatch']?.data.id || {}}
                handleClose={handleCloseDialog}
            />

            <ProvideDeliveryDateDialog
                open={openDialog['provide-delivery-date']?.open || false}
                dispatchData={openDialog['provide-delivery-date']?.data || {}}
                handleClose={handleCloseDialog}
            />

            <ConfirmDialog
                open={openDialog['confirm-dialog']?.open || false}
                props={confirmDialog}
            />

            {/* Main Content */}
            <MDBox pt={6} pb={3}>
                <Card>

                    {/* Card Header */}
                    <MDBox p={3} lineHeight={1}>
                        <MDTypography variant="h5" fontWeight="medium">
                            Manage Dispatches
                        </MDTypography>
                    </MDBox>

                    {/* Data Table */}
                    <DataTable
                        table={dataTableData}
                        isLoading={isLoading}
                        entriesPerPage
                        showTotalEntries
                        canSearch
                        tableId="manage-dispatches-table"
                    />

                </Card>
            </MDBox>
            <MDBox>
                <Footer />
            </MDBox>
        </DashboardLayout>
    )
}